/**
 * TanStack Query wiring for the Bench.
 *
 * The job list is the one query in the product with its own fast timer: while any job is
 * queued or printing it refetches every 5 seconds, because the person standing at the
 * printer wants to see "Printing" turn into "Done" without touching anything. Idle, it
 * falls back to the live class cadence.
 *
 * Nothing here is optimistic. A print job leaves the building.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { qk } from '@/lib/keys';
import { benchApi } from './api';
import { hasActiveJob } from './labels';
import type { VialEnqueueRequest } from './types';

export const JOBS_ACTIVE_INTERVAL_MS = 5_000;
export const JOBS_IDLE_INTERVAL_MS = 30_000;

export function useVialProducts() {
  return useQuery({
    queryKey: qk.benchProducts(),
    queryFn: () => benchApi.products(),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useVialJobs() {
  return useQuery({
    queryKey: qk.benchJobs(),
    queryFn: () => benchApi.jobs(50),
    staleTime: 0,
    refetchInterval: (query) =>
      hasActiveJob(query.state.data?.jobs) ? JOBS_ACTIVE_INTERVAL_MS : JOBS_IDLE_INTERVAL_MS,
  });
}

export function useEnqueueVialJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: VialEnqueueRequest & { idempotencyKey: string }) =>
      benchApi.enqueue({ coa_id: input.coa_id, qty: input.qty }, input.idempotencyKey),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.benchJobs() }),
  });
}

export function useCancelVialJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: number) => benchApi.cancel(jobId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.benchJobs() }),
  });
}
